<template>
  <b-modal
    id="split_account_payable_modal"
    :title="$t('Dividir despesa')"
    centered
    size="xl"
    no-close-on-backdrop
    hide-header-close
    hide-footer
    @hidden="cleanModalData"
  >
    <FormulateForm
      ref="modalSplitAccountPayableForm"
      name="modalSplitAccountPayableForm"
    >
      <b-card-actions
        :title="$t('Despesa a ser dividida')"
      >
        <b-row>
          <b-col md="1">
            <FormulateInput
              :id="`modal_split-origin_statement_date`"
              :value="getOriginInfo.statementDate"
              label="Emis."
              type="label"
              filter="date"
            />
          </b-col>
          <b-col md="1">
            <FormulateInput
              :id="`modal_split-origin_due_date`"
              :value="getOriginInfo.dueDate"
              label="Venc."
              type="label"
              filter="date"
            />
          </b-col>
          <b-col md="1">
            <FormulateInput
              :id="`modal_split-origin_store`"
              :value="getOriginInfo.store"
              label="Loja"
              type="label"
              filter="storeId"
            />
          </b-col>
          <b-col md="4">
            <e-person-search
              v-if="getOriginInfo.personValue"
              id="modal_split-origin_person"
              :value="getOriginInfo.personValue"
              value-is-object
              is-read-only
              label="Fornecedor/Cliente"
              :is-customer="getOriginInfo.personType === 'customer'"
              :is-supplier="getOriginInfo.personType === 'supplier'"
              :default-options="[getOriginInfo.personValue]"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              :id="`modal_split-origin_description`"
              :value="getOriginInfo.description"
              label="Descrição"
              type="label"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              :id="`modal_split-origin_value`"
              :value="getOriginInfo.value"
              label="Valor"
              type="label"
              filter="currency"
            />
          </b-col>
          <b-col md="3">
            <e-payment-method
              v-if="getOriginInfo.paymentMethod"
              :id="`modal_split-origin_payment_method`"
              :value="getOriginInfo.paymentMethod"
              value-is-object
              is-read-only
              label="Forma de pagamento"
            />
          </b-col>

          <b-col md="2">
            <FormulateInput
              :id="`modal_split-origin_document_type`"
              :value="getOriginInfo.documentType"
              label="Tipo documento"
              type="label"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              :id="`modal_split-origin_document`"
              :value="getOriginInfo.document"
              label="Nº Doc"
              type="label"
            />
          </b-col>
          <b-col md="2">
            <e-management-account
              v-if="getOriginInfo.managementAccount"
              id="modal_split-origin_management_account"
              :value="getOriginInfo.managementAccount.id"
              is-read-only
              type="Output"
              name="modal_split-origin_management_account"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        no-actions
        :show-loading="saving"
      >
        <b-row>
          <b-col md="2">
            <FormulateInput
              :id="`modal_split-split_quantity`"
              v-model="localForm.splitQuantity"
              label="Quant. a dividir"
              type="text-number"
              @input="onFillInstallments"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <accounts-payable-split-table
              ref="modalSplitTableRef"
              :installment-origin="installmentOrigin"
              :installment-splitted-list="installmentSplitted"
              @remove="onRemoveInstallment"
            />
          </b-col>
        </b-row>
      </b-card-actions>
    </FormulateForm>

    <b-row class="mt-1 justify-content-end">
      <b-col class="d-flex justify-content-end">
        <e-button
          id="modal_pay_installment-btn_cancel"
          class="mr-1"
          variant="outline-primary"
          :text="$t('Cancelar')"
          :text-shortcuts="['ESC']"
          @click="hideModal"
        />

        <e-button
          id="modal_pay_installment-btn_confirm"
          variant="primary"
          icon="arrows-expand"
          :text="$t('Confirmar divisão')"
          :busy="saving"
          @click="onConfirm"
        />
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { BModal, BRow, BCol} from 'bootstrap-vue'
import EButton from '@/views/components/EButton.vue'
import { financialDomain, formulateHelper } from '@/mixins'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import EPaymentMethod from '@/views/components/inputs/EPaymentMethod.vue'
import EManagementAccount from '@/views/components/inputs/EManagementAccount.vue'
import EPersonSearch from '@/views/components/inputs/EPersonSearch.vue'
import { debounce } from 'lodash'
import AccountsPayableSplitTable from './AccountsPayableSplitTable.vue'

const getInitialLocalForm = () => ({
  splitQuantity: 0,
})

export default {
  components: {
    BModal,
    BRow,
    BCol,
    EButton,
    BCardActions,
    EPaymentMethod,
    EManagementAccount,
    EPersonSearch,
    AccountsPayableSplitTable,
  },

  mixins: [financialDomain, formulateHelper],

  props: {},

  data() {
    return {
      loading: false,
      saving: false,
      localForm: getInitialLocalForm(),
      installmentOrigin: null,
      installmentSplitted: [],
      accountsPayableRowVersion: null,
    }
  },

  computed: {
    getOriginInfo() {
      const accPayableOrigin = this.installmentOrigin?.accountsPayable || {}

      return {
        description: accPayableOrigin.description,
        personType: accPayableOrigin.supplier ? 'supplier' : 'customer',
        personValue: accPayableOrigin.supplier || accPayableOrigin.customer,
        store: accPayableOrigin.store,
        statementDate: accPayableOrigin.statementDate,
        dueDate: this.installmentOrigin?.dueDate,
        documentType: accPayableOrigin.documentType,
        document: accPayableOrigin.document,
        value: accPayableOrigin.value,
        paymentMethod: this.installmentOrigin?.paymentMethod,
        managementAccount: accPayableOrigin.managementAccount,
      }
    },
  },

  methods: {
    async onConfirm() {
      this.$refs.modalSplitAccountPayableForm.showErrors()
      if (this.$refs.modalSplitAccountPayableForm.hasErrors) {
        this.showInvalidDataMessage()
        return
      }

      this.saving = true
      try {
        const payload = {
          accountPayableId: this.installmentOrigin.accountsPayable.id,
          accountsPayableSplitItems: this.installmentSplitted,
        }
        await this.$http.post('/api/accounting/accounts-payable/split', payload)

        this.showSuccess({})

        this.$emit('after-confirm')
        this.hideModal()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    async showModal(installment) {
      this.loading = true
      this.$bvModal.show('split_account_payable_modal')

      this.installmentOrigin = installment
      this.accountsPayableRowVersion = installment.accountsPayable.rowVersion

      this.$nextTick(() => {
        this.focusInput('#modal_split-split_quantity')
      })
    },
    cleanModalData() {
      this.installmentOrigin = null
      this.localForm = getInitialLocalForm()
      this.installmentSplitted = []
      this.accountsPayableRowVersion = null
    },
    hideModal() {
      this.cleanModalData()
      this.$bvModal.hide('split_account_payable_modal')
    },

    // eslint-disable-next-line func-names
    onFillInstallments: debounce(function() {
      if (!this.$refs.modalSplitTableRef?.getSplittedItems) return

      const splittedItems = this.$refs.modalSplitTableRef.getSplittedItems(this.localForm.splitQuantity ?? 0, {
        paymentMethodId: this.getOriginInfo.paymentMethod?.id,
        managementAccountId: this.getOriginInfo.managementAccount?.id,
        documentType: this.getOriginInfo.documentType,
      })

      this.installmentSplitted = splittedItems
    }, 300),

    onRemoveInstallment(item) {
      const indexFound = this.installmentSplitted.findIndex(
        installment => installment.localId === item.localId
      )
      this.installmentSplitted.splice(indexFound, 1)
    },
  },
}
</script>

<style lang="scss" scoped></style>
